import type { FtpSelectionResult } from "../athlete-profile/ftpSelection";
import type { Ride } from "../ride-center/types/ride";
import type { ActivitySession } from "./activitySources";
import type { UnifiedActivityAnalysisResult } from "./sessionAnalysis";

export const COMPLETED_ACTIVITY_HISTORY_VERSION = 1;
const HISTORY_DB_NAME = "johan-performance-lab-train-history";
const HISTORY_DB_VERSION = 1;
const HISTORY_STORE_NAME = "completed-activity-history";

export type CompletedActivityMetricName =
  | "variability_index"
  | "aerobic_decoupling"
  | "normalized_power"
  | "intensity_factor"
  | "first_half_vs_second_half_power"
  | "durability60"
  | "durability90"
  | "durability120"
  | "cadence_stability"
  | "average_minute_ventilation"
  | "maximum_minute_ventilation"
  | "average_breathing_frequency"
  | "respiratory_drift"
  | "ventilation_to_power_relationship"
  | "respiratory_coverage";

export type HistoricalSourceType = "Garmin FIT" | "TrainingPeaks FIT" | "TrainingPeaks FIT.gz" | "Tymewear CSV" | "unknown";

export type SourceProvenanceEntry = {
  sourceKey: string;
  sourceType: HistoricalSourceType;
  sourceFileName: string | null;
  sourceFileSizeBytes: number | null;
  sourceActivityId: string | null;
  importedAt: string;
  importMode: "interactive_analysis" | "historical_batch_analysis";
  confidence: "high" | "medium" | "low";
};

export type ImportMetadata = {
  importedAt: string;
  importMode: "interactive_analysis" | "historical_batch_analysis";
  sourceType: HistoricalSourceType;
  sourceFileName: string | null;
  sourceActivityId: string | null;
  provenance: string;
  confidence: "high" | "medium" | "low";
  ftpResolution: "historical_snapshot" | "ftp_history" | "profile_fallback";
  historicalFtpFallbackApplied: boolean;
};

export type CompletedActivityRecord = {
  schemaVersion: number;
  activityId: string;
  activityDate: string;
  activityTitle: string;
  activityType: string;
  rideClassification: {
    type: UnifiedActivityAnalysisResult["rideClassification"]["type"];
    displayName: string;
    confidencePercent: number;
    structured: boolean;
  };
  activityContext: {
    indoorOutdoor: "indoor" | "outdoor" | "unknown";
    surface: "road" | "gravel" | "unknown";
  };
  durationSeconds: number | null;
  distanceMeters: number | null;
  elevationGainMeters: number | null;
  movingTimeSeconds: number | null;
  elapsedTimeSeconds: number | null;
  ftpSnapshot: {
    ftpWatts: number | null;
    sourceLabel: string | null;
    effectiveDateIso: string | null;
    policyKind: string | null;
  };
  averagePower: number | null;
  normalizedPower: number | null;
  intensityFactor: number | null;
  trainingStressScore: number | null;
  variabilityIndex: number | null;
  averageHeartRate: number | null;
  aerobicDecoupling: number | null;
  firstHalfPower: number | null;
  secondHalfPower: number | null;
  durability60: number | null;
  durability90: number | null;
  durability120: number | null;
  averageCadence: number | null;
  cadenceStability: number | null;
  powerZoneDistribution: Array<{ zoneName: string; seconds: number | null; percentageOfMovingTime: number | null }>;
  respiratorySummary: {
    available: boolean;
    sourceType: "tymewear" | "none";
    coveragePercent: number | null;
    averageMinuteVentilation: number | null;
    maximumMinuteVentilation: number | null;
    averageBreathingFrequency: number | null;
    respiratoryDriftPercent: number | null;
    ventilationToPowerRelationship: number | null;
    alignmentConfidence: "high" | "medium" | "low" | "unavailable";
  };
  thermalSummary: {
    available: boolean;
    note: string;
  };
  hydrationSummary: {
    available: boolean;
    note: string;
  };
  sourceCoverage: {
    activityCompletenessPercent: number;
    ridePresent: boolean;
    respiratoryPresent: boolean;
    respiratoryUsable: boolean;
    hydrationPresent: boolean;
    thermalPresent: boolean;
    dataDomainCount: number;
  };
  analysisConfidence: {
    overallPercent: number;
    rideClassificationPercent: number;
    enduranceExecutionPercent: number | null;
    respiratoryAnalysisPercent: number | null;
  };
  analysisVersion: string;
  analysisVersionHistory: string[];
  duplicateIdentity: {
    startTimestampMs: number | null;
    durationSeconds: number | null;
    distanceMeters: number | null;
    sourceFingerprint: string | null;
    stableSourceHash: string;
  };
  sourceProvenance?: SourceProvenanceEntry[];
  importMetadata?: ImportMetadata;
  maximumPower?: number | null;
  workKilojoules?: number | null;
  createdAt: string;
  updatedAt: string;
};

export type DuplicateIdentityResult = {
  strategy: "activity_id" | "stable_source_hash" | "timestamp_duration_distance" | "canonical_match" | "none";
  matchedActivityId: string | null;
  mergedWithExisting: boolean;
  createdNewRecordBecauseIdConflict: boolean;
};

export type SaveCompletedActivityRecordResult = {
  backend: "indexeddb" | "memory";
  databaseStatus: "unknown" | "opening" | "open" | "failed";
  record: CompletedActivityRecord;
  duplicateIdentity: DuplicateIdentityResult;
};

type HistoryStoreStatus = "unknown" | "opening" | "open" | "failed";
type OpenStoreResult = {
  backend: "indexeddb" | "memory";
  databaseStatus: HistoryStoreStatus;
  database: IDBDatabase | null;
};

export class CompletedActivityHistoryStoreUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "CompletedActivityHistoryStoreUnavailableError";
  }
}

const memoryHistoryStore = new Map<string, CompletedActivityRecord>();
let historyBackend: "indexeddb" | "memory" = "indexeddb";
let historyDatabaseStatus: HistoryStoreStatus = "unknown";
let historyDatabase: IDBDatabase | null = null;
let historyInitialization: Promise<OpenStoreResult> | null = null;

function finite(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function toIsoDate(value: Ride["rideDate"]): string {
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }
  return new Date().toISOString();
}

function inferIndoorOutdoor(ride: Ride): "indoor" | "outdoor" | "unknown" {
  const hasGps = ride.records.some(record => finite(record.latitudeDegrees) !== null && finite(record.longitudeDegrees) !== null);
  if (hasGps) return "outdoor";
  if (ride.records.length > 0) return "indoor";
  return "unknown";
}

function inferSurface(sourceFileName: string | null): "road" | "gravel" | "unknown" {
  const normalized = (sourceFileName ?? "").toLowerCase();
  if (!normalized) return "unknown";
  if (normalized.includes("gravel")) return "gravel";
  if (normalized.includes("road") || normalized.includes("crit") || normalized.includes("race")) return "road";
  return "unknown";
}

export function computeStableSourceHash(input: {
  sourceFingerprint: string | null;
  sourceFileName: string | null;
  sourceFileSizeBytes: number | null;
  startTimestampMs: number | null;
  durationSeconds: number | null;
  distanceMeters: number | null;
}): string {
  const parts = [
    input.sourceFingerprint ?? "none",
    (input.sourceFileName ?? "none").toLowerCase(),
    String(input.sourceFileSizeBytes ?? "none"),
    String(input.startTimestampMs ?? "none"),
    String(input.durationSeconds ?? "none"),
    String(input.distanceMeters ?? "none")
  ];
  return parts.join("::");
}

function durationDifferenceRatio(left: number | null, right: number | null): number | null {
  if (left === null || right === null || Math.max(Math.abs(left), Math.abs(right)) === 0) return null;
  return Math.abs(left - right) / Math.max(Math.abs(left), Math.abs(right));
}

function distanceDifferenceRatio(left: number | null, right: number | null): number | null {
  if (left === null || right === null || Math.max(Math.abs(left), Math.abs(right)) === 0) return null;
  return Math.abs(left - right) / Math.max(Math.abs(left), Math.abs(right));
}

function sourceTypeFromFileName(fileName: string | null): HistoricalSourceType {
  const normalized = (fileName ?? "").toLowerCase();
  if (!normalized) return "unknown";
  if (normalized.endsWith(".fit.gz") && normalized.includes("trainingpeaks")) return "TrainingPeaks FIT.gz";
  if (normalized.endsWith(".fit.gz")) return "TrainingPeaks FIT.gz";
  if (normalized.includes("trainingpeaks")) return "TrainingPeaks FIT";
  if (normalized.endsWith(".fit")) return "Garmin FIT";
  if (normalized.endsWith(".csv") && normalized.includes("tymewear")) return "Tymewear CSV";
  return "unknown";
}

function mergeSourceProvenance(left: SourceProvenanceEntry[] | undefined, right: SourceProvenanceEntry[] | undefined): SourceProvenanceEntry[] {
  const all = [...(left ?? []), ...(right ?? [])];
  const byKey = new Map<string, SourceProvenanceEntry>();
  for (const item of all) {
    const existing = byKey.get(item.sourceKey);
    if (!existing) {
      byKey.set(item.sourceKey, item);
      continue;
    }
    if (Date.parse(item.importedAt) > Date.parse(existing.importedAt)) {
      byKey.set(item.sourceKey, item);
    }
  }
  return [...byKey.values()];
}

function canonicalMatchScore(left: CompletedActivityRecord, right: CompletedActivityRecord): { score: number; highConfidence: boolean } {
  const leftStart = left.duplicateIdentity.startTimestampMs;
  const rightStart = right.duplicateIdentity.startTimestampMs;
  if (leftStart === null || rightStart === null) {
    return { score: 0, highConfidence: false };
  }

  const startDeltaSeconds = Math.abs(leftStart - rightStart) / 1000;
  const durationDiff = durationDifferenceRatio(left.duplicateIdentity.durationSeconds, right.duplicateIdentity.durationSeconds);
  const distanceDiff = distanceDifferenceRatio(left.duplicateIdentity.distanceMeters, right.duplicateIdentity.distanceMeters);
  const sameSport = left.activityType === right.activityType;

  let score = 0;
  if (startDeltaSeconds <= 90) score += 50;
  else if (startDeltaSeconds <= 180) score += 35;
  else if (startDeltaSeconds <= 300) score += 15;

  if (durationDiff !== null && durationDiff <= 0.02) score += 20;
  else if (durationDiff !== null && durationDiff <= 0.05) score += 10;

  if (distanceDiff !== null && distanceDiff <= 0.02) score += 20;
  else if (distanceDiff !== null && distanceDiff <= 0.05) score += 10;

  if (sameSport) score += 10;

  const highConfidence = score >= 85 && startDeltaSeconds <= 180;
  return { score, highConfidence };
}

function sameIdentity(left: CompletedActivityRecord, right: CompletedActivityRecord): boolean {
  if (left.duplicateIdentity.stableSourceHash === right.duplicateIdentity.stableSourceHash) {
    return true;
  }

  const leftStart = left.duplicateIdentity.startTimestampMs;
  const rightStart = right.duplicateIdentity.startTimestampMs;
  if (leftStart === null || rightStart === null) {
    return false;
  }

  const startDeltaSeconds = Math.abs(leftStart - rightStart) / 1000;
  const durationDiff = durationDifferenceRatio(left.duplicateIdentity.durationSeconds, right.duplicateIdentity.durationSeconds);
  const distanceDiff = distanceDifferenceRatio(left.duplicateIdentity.distanceMeters, right.duplicateIdentity.distanceMeters);

  return startDeltaSeconds <= 120
    && (durationDiff === null || durationDiff <= 0.02)
    && (distanceDiff === null || distanceDiff <= 0.02);
}

function cloneRecord(record: CompletedActivityRecord): CompletedActivityRecord {
  return {
    ...record,
    rideClassification: { ...record.rideClassification },
    activityContext: { ...record.activityContext },
    ftpSnapshot: { ...record.ftpSnapshot },
    powerZoneDistribution: [...record.powerZoneDistribution],
    respiratorySummary: { ...record.respiratorySummary },
    thermalSummary: { ...record.thermalSummary },
    hydrationSummary: { ...record.hydrationSummary },
    sourceCoverage: { ...record.sourceCoverage },
    analysisConfidence: { ...record.analysisConfidence },
    analysisVersionHistory: [...record.analysisVersionHistory],
    duplicateIdentity: { ...record.duplicateIdentity },
    sourceProvenance: record.sourceProvenance ? [...record.sourceProvenance] : undefined,
    importMetadata: record.importMetadata ? { ...record.importMetadata } : undefined
  };
}

export function createCompletedActivityRecordFromSnapshot(input: {
  activityId: string;
  activitySession: ActivitySession;
  parsedRide: Ride;
  analysis: UnifiedActivityAnalysisResult;
  analysisFtpSelection: FtpSelectionResult | null;
  completedAt: string | null;
}): CompletedActivityRecord {
  const nowIso = new Date().toISOString();
  const activityDateIso = toIsoDate(input.parsedRide.rideDate);
  const startTimestampMs = Date.parse(activityDateIso);
  const endurance = input.analysis.enduranceExecution;
  const respiratory = input.analysis.respiratoryAnalysis;
  const fileName = input.parsedRide.sourceFileName ?? input.activitySession.primary?.file.name ?? null;
  const sourceFingerprint = input.activitySession.primary?.fingerprint ?? null;
  const stableSourceHash = computeStableSourceHash({
    sourceFingerprint,
    sourceFileName: fileName,
    sourceFileSizeBytes: input.parsedRide.sourceFileSizeBytes,
    startTimestampMs: Number.isNaN(startTimestampMs) ? null : startTimestampMs,
    durationSeconds: input.parsedRide.elapsedTime ?? input.parsedRide.movingTime ?? null,
    distanceMeters: input.parsedRide.distance ?? null
  });

  const durabilityMap = new Map((endurance?.durability ?? []).map(item => [item.afterMinutes, item.powerChangePercent]));
  const coverageCategories = input.analysis.input.dataCoverage.categories;
  const hasDomain = (domain: string) => coverageCategories.some(category => category.domain === domain && category.present);
  const hasUsableDomain = (domain: string) => coverageCategories.some(category => category.domain === domain && category.usable);

  return {
    schemaVersion: COMPLETED_ACTIVITY_HISTORY_VERSION,
    activityId: input.activityId,
    activityDate: activityDateIso,
    activityTitle: `${input.analysis.rideClassification.displayName} · ${new Date(activityDateIso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`,
    activityType: "cycling",
    rideClassification: {
      type: input.analysis.rideClassification.type,
      displayName: input.analysis.rideClassification.displayName,
      confidencePercent: input.analysis.rideClassification.confidencePercent,
      structured: input.analysis.rideClassification.showStructuredIntervals
    },
    activityContext: {
      indoorOutdoor: inferIndoorOutdoor(input.parsedRide),
      surface: inferSurface(fileName)
    },
    durationSeconds: input.parsedRide.elapsedTime ?? input.parsedRide.movingTime ?? null,
    distanceMeters: input.parsedRide.distance ?? null,
    elevationGainMeters: input.parsedRide.elevationGain ?? null,
    movingTimeSeconds: input.parsedRide.movingTime ?? null,
    elapsedTimeSeconds: input.parsedRide.elapsedTime ?? null,
    ftpSnapshot: {
      ftpWatts: input.analysisFtpSelection?.ftpWatts ?? null,
      sourceLabel: input.analysisFtpSelection?.sourceLabel ?? null,
      effectiveDateIso: input.analysisFtpSelection?.effectiveDateIso ?? null,
      policyKind: input.analysisFtpSelection?.selectedPolicy.kind ?? null
    },
    averagePower: input.parsedRide.averagePower ?? input.parsedRide.rideAnalytics?.averagePower ?? null,
    normalizedPower: input.parsedRide.rideAnalytics?.normalizedPower ?? input.parsedRide.normalizedPower ?? null,
    intensityFactor: input.parsedRide.rideAnalytics?.intensityFactor ?? input.parsedRide.if ?? null,
    trainingStressScore: input.parsedRide.rideAnalytics?.trainingStressScore ?? input.parsedRide.tss ?? null,
    variabilityIndex: input.parsedRide.rideAnalytics?.variabilityIndex ?? input.parsedRide.vi ?? null,
    averageHeartRate: input.parsedRide.averageHeartRate ?? null,
    aerobicDecoupling: endurance?.aerobicDecouplingPercent ?? null,
    firstHalfPower: endurance?.firstHalfPower ?? null,
    secondHalfPower: endurance?.secondHalfPower ?? null,
    durability60: durabilityMap.get(60) ?? null,
    durability90: durabilityMap.get(90) ?? null,
    durability120: durabilityMap.get(120) ?? null,
    averageCadence: input.parsedRide.averageCadence ?? null,
    cadenceStability: endurance?.cadenceStabilityPercent ?? null,
    powerZoneDistribution: input.parsedRide.rideAnalytics?.powerZones ?? [],
    maximumPower: input.parsedRide.maximumPower ?? null,
    workKilojoules: input.parsedRide.rideAnalytics?.workKilojoules ?? null,
    respiratorySummary: {
      available: respiratory !== null,
      sourceType: respiratory ? "tymewear" : "none",
      coveragePercent: respiratory?.dataCoveragePercent ?? null,
      averageMinuteVentilation: respiratory?.metrics.minuteVentilation?.average ?? null,
      maximumMinuteVentilation: respiratory?.metrics.minuteVentilation?.maximum ?? null,
      averageBreathingFrequency: respiratory?.metrics.breathingFrequency?.average ?? null,
      respiratoryDriftPercent: respiratory?.metrics.minuteVentilation?.driftPercent ?? null,
      ventilationToPowerRelationship: respiratory?.ventilationToPowerCorrelation ?? null,
      alignmentConfidence: respiratory?.alignmentConfidence ?? "unavailable"
    },
    thermalSummary: {
      available: input.analysis.input.primaryThermalDetected || input.analysis.input.thermalSources.length > 0,
      note: input.analysis.input.primaryThermalDetected
        ? "CORE developer-field temperature detected in primary FIT"
        : input.analysis.input.thermalSources.length > 0
          ? "Thermal companion source present"
          : "No thermal source"
    },
    hydrationSummary: {
      available: input.analysis.input.hydrationSources.length > 0,
      note: input.analysis.input.hydrationSources.length > 0 ? "Hydration companion source present" : "No hydration source"
    },
    sourceCoverage: {
      activityCompletenessPercent: input.analysis.activityCompletenessPercent,
      ridePresent: hasDomain("ride"),
      respiratoryPresent: hasDomain("respiratory"),
      respiratoryUsable: hasUsableDomain("respiratory"),
      hydrationPresent: hasDomain("hydration"),
      thermalPresent: hasDomain("thermal"),
      dataDomainCount: coverageCategories.filter(category => category.present).length
    },
    analysisConfidence: {
      overallPercent: input.analysis.analysisConfidence.overallPercent,
      rideClassificationPercent: input.analysis.analysisConfidence.rideClassificationPercent,
      enduranceExecutionPercent: input.analysis.analysisConfidence.enduranceExecutionPercent,
      respiratoryAnalysisPercent: input.analysis.analysisConfidence.respiratoryAnalysisPercent
    },
    analysisVersion: input.analysis.analysisVersion,
    analysisVersionHistory: [input.analysis.analysisVersion],
    duplicateIdentity: {
      startTimestampMs: Number.isNaN(startTimestampMs) ? null : startTimestampMs,
      durationSeconds: input.parsedRide.elapsedTime ?? input.parsedRide.movingTime ?? null,
      distanceMeters: input.parsedRide.distance ?? null,
      sourceFingerprint,
      stableSourceHash
    },
    sourceProvenance: [{
      sourceKey: stableSourceHash,
      sourceType: sourceTypeFromFileName(fileName),
      sourceFileName: fileName,
      sourceFileSizeBytes: input.parsedRide.sourceFileSizeBytes,
      sourceActivityId: null,
      importedAt: nowIso,
      importMode: "interactive_analysis",
      confidence: "high"
    }],
    importMetadata: {
      importedAt: nowIso,
      importMode: "interactive_analysis",
      sourceType: sourceTypeFromFileName(fileName),
      sourceFileName: fileName,
      sourceActivityId: null,
      provenance: "interactive_analysis",
      confidence: "high",
      ftpResolution: "profile_fallback",
      historicalFtpFallbackApplied: false
    },
    createdAt: input.completedAt ?? nowIso,
    updatedAt: nowIso
  };
}

async function initializeHistoryStore(): Promise<OpenStoreResult> {
  if (historyDatabaseStatus === "open" && historyDatabase) {
    return { backend: historyBackend, databaseStatus: historyDatabaseStatus, database: historyDatabase };
  }
  if (historyInitialization) {
    return historyInitialization;
  }

  historyDatabaseStatus = "opening";

  if (typeof indexedDB === "undefined") {
    historyBackend = "memory";
    historyDatabaseStatus = "failed";
    return { backend: historyBackend, databaseStatus: historyDatabaseStatus, database: null };
  }

  historyInitialization = new Promise(resolve => {
    const request = indexedDB.open(HISTORY_DB_NAME, HISTORY_DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(HISTORY_STORE_NAME)) {
        database.createObjectStore(HISTORY_STORE_NAME, { keyPath: "activityId" });
      }
    };
    request.onerror = () => {
      historyBackend = "indexeddb";
      historyDatabaseStatus = "failed";
      historyDatabase = null;
      historyInitialization = null;
      resolve({ backend: historyBackend, databaseStatus: historyDatabaseStatus, database: null });
    };
    request.onblocked = () => {
      historyBackend = "indexeddb";
      historyDatabaseStatus = "failed";
      historyDatabase = null;
      historyInitialization = null;
      resolve({ backend: historyBackend, databaseStatus: historyDatabaseStatus, database: null });
    };
    request.onsuccess = () => {
      historyBackend = "indexeddb";
      historyDatabaseStatus = "open";
      historyDatabase = request.result;
      historyDatabase.onclose = () => {
        historyDatabaseStatus = "failed";
        historyDatabase = null;
      };
      historyInitialization = null;
      resolve({ backend: historyBackend, databaseStatus: historyDatabaseStatus, database: historyDatabase });
    };
  });

  return historyInitialization;
}

async function listIndexedDbRecords(database: IDBDatabase): Promise<CompletedActivityRecord[]> {
  return new Promise(resolve => {
    const transaction = database.transaction(HISTORY_STORE_NAME, "readonly");
    const store = transaction.objectStore(HISTORY_STORE_NAME);
    const request = store.getAll();
    request.onerror = () => resolve([]);
    request.onsuccess = () => {
      const records = (request.result as CompletedActivityRecord[]).filter(Boolean).map(cloneRecord);
      resolve(records);
    };
  });
}

async function putIndexedDbRecord(database: IDBDatabase, record: CompletedActivityRecord): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(HISTORY_STORE_NAME, "readwrite");
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Unable to write history record."));
    const store = transaction.objectStore(HISTORY_STORE_NAME);
    store.put(record);
  });
}

export async function listCompletedActivityHistoryRecords(): Promise<CompletedActivityRecord[]> {
  const opened = await initializeHistoryStore();
  if (opened.database) {
    const records = await listIndexedDbRecords(opened.database);
    return records.sort((left, right) => Date.parse(right.activityDate) - Date.parse(left.activityDate));
  }

  if (opened.backend === "indexeddb") {
    throw new CompletedActivityHistoryStoreUnavailableError("Completed activity history store is unavailable.");
  }

  return [...memoryHistoryStore.values()].map(cloneRecord).sort((left, right) => Date.parse(right.activityDate) - Date.parse(left.activityDate));
}

async function persistRecord(record: CompletedActivityRecord): Promise<{ backend: "indexeddb" | "memory"; status: HistoryStoreStatus }> {
  const opened = await initializeHistoryStore();
  if (opened.database) {
    await putIndexedDbRecord(opened.database, record);
    return { backend: opened.backend, status: opened.databaseStatus };
  }

  if (opened.backend === "indexeddb") {
    throw new CompletedActivityHistoryStoreUnavailableError("Completed activity history store is unavailable.");
  }

  memoryHistoryStore.set(record.activityId, cloneRecord(record));
  return { backend: opened.backend, status: opened.databaseStatus };
}

export async function deleteCompletedActivityHistoryRecord(activityId: string): Promise<void> {
  const opened = await initializeHistoryStore();
  if (opened.database) {
    await new Promise<void>(resolve => {
      const transaction = opened.database!.transaction(HISTORY_STORE_NAME, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      const store = transaction.objectStore(HISTORY_STORE_NAME);
      store.delete(activityId);
    });
    return;
  }

  if (opened.backend === "indexeddb") {
    throw new CompletedActivityHistoryStoreUnavailableError("Completed activity history store is unavailable.");
  }

  memoryHistoryStore.delete(activityId);
}

export async function clearCompletedActivityHistoryRecords(): Promise<void> {
  const opened = await initializeHistoryStore();
  if (opened.database) {
    await new Promise<void>(resolve => {
      const transaction = opened.database!.transaction(HISTORY_STORE_NAME, "readwrite");
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => resolve();
      const store = transaction.objectStore(HISTORY_STORE_NAME);
      store.clear();
    });
    return;
  }

  if (opened.backend === "indexeddb") {
    throw new CompletedActivityHistoryStoreUnavailableError("Completed activity history store is unavailable.");
  }

  memoryHistoryStore.clear();
}

export async function saveCompletedActivityRecord(record: CompletedActivityRecord): Promise<SaveCompletedActivityRecordResult> {
  const existing = await listCompletedActivityHistoryRecords();
  const nowIso = new Date().toISOString();

  const exactIdMatch = existing.find(candidate => candidate.activityId === record.activityId);
  if (exactIdMatch) {
    if (sameIdentity(exactIdMatch, record)) {
      const merged: CompletedActivityRecord = {
        ...record,
        activityId: exactIdMatch.activityId,
        createdAt: exactIdMatch.createdAt,
        updatedAt: nowIso,
        analysisVersionHistory: Array.from(new Set([...exactIdMatch.analysisVersionHistory, exactIdMatch.analysisVersion, record.analysisVersion])),
        sourceProvenance: mergeSourceProvenance(exactIdMatch.sourceProvenance, record.sourceProvenance)
      };
      const persisted = await persistRecord(merged);
      return {
        backend: persisted.backend,
        databaseStatus: persisted.status,
        record: merged,
        duplicateIdentity: {
          strategy: "activity_id",
          matchedActivityId: exactIdMatch.activityId,
          mergedWithExisting: true,
          createdNewRecordBecauseIdConflict: false
        }
      };
    }

    const forked: CompletedActivityRecord = {
      ...record,
      activityId: `${record.activityId}::${Date.now()}`,
      createdAt: nowIso,
      updatedAt: nowIso,
      analysisVersionHistory: [record.analysisVersion]
    };
    const persisted = await persistRecord(forked);
    return {
      backend: persisted.backend,
      databaseStatus: persisted.status,
      record: forked,
      duplicateIdentity: {
        strategy: "none",
        matchedActivityId: null,
        mergedWithExisting: false,
        createdNewRecordBecauseIdConflict: true
      }
    };
  }

  const stableHashMatch = existing.find(candidate => candidate.duplicateIdentity.stableSourceHash === record.duplicateIdentity.stableSourceHash);
  if (stableHashMatch) {
    const merged: CompletedActivityRecord = {
      ...record,
      activityId: stableHashMatch.activityId,
      createdAt: stableHashMatch.createdAt,
      updatedAt: nowIso,
      analysisVersionHistory: Array.from(new Set([...stableHashMatch.analysisVersionHistory, stableHashMatch.analysisVersion, record.analysisVersion])),
      sourceProvenance: mergeSourceProvenance(stableHashMatch.sourceProvenance, record.sourceProvenance)
    };
    const persisted = await persistRecord(merged);
    return {
      backend: persisted.backend,
      databaseStatus: persisted.status,
      record: merged,
      duplicateIdentity: {
        strategy: "stable_source_hash",
        matchedActivityId: stableHashMatch.activityId,
        mergedWithExisting: true,
        createdNewRecordBecauseIdConflict: false
      }
    };
  }

  const temporalMatch = existing.find(candidate => {
    if (!sameIdentity(candidate, record)) {
      return false;
    }
    return true;
  });
  if (temporalMatch) {
    const canonical = canonicalMatchScore(temporalMatch, record);
    if (!canonical.highConfidence) {
      const freshUncertain: CompletedActivityRecord = {
        ...record,
        createdAt: record.createdAt || nowIso,
        updatedAt: nowIso,
        analysisVersionHistory: record.analysisVersionHistory.length > 0 ? record.analysisVersionHistory : [record.analysisVersion]
      };
      const persistedUncertain = await persistRecord(freshUncertain);
      return {
        backend: persistedUncertain.backend,
        databaseStatus: persistedUncertain.status,
        record: freshUncertain,
        duplicateIdentity: {
          strategy: "none",
          matchedActivityId: null,
          mergedWithExisting: false,
          createdNewRecordBecauseIdConflict: false
        }
      };
    }

    const merged: CompletedActivityRecord = {
      ...record,
      activityId: temporalMatch.activityId,
      createdAt: temporalMatch.createdAt,
      updatedAt: nowIso,
      analysisVersionHistory: Array.from(new Set([...temporalMatch.analysisVersionHistory, temporalMatch.analysisVersion, record.analysisVersion])),
      sourceProvenance: mergeSourceProvenance(temporalMatch.sourceProvenance, record.sourceProvenance)
    };
    const persisted = await persistRecord(merged);
    return {
      backend: persisted.backend,
      databaseStatus: persisted.status,
      record: merged,
      duplicateIdentity: {
        strategy: "canonical_match",
        matchedActivityId: temporalMatch.activityId,
        mergedWithExisting: true,
        createdNewRecordBecauseIdConflict: false
      }
    };
  }

  const fresh: CompletedActivityRecord = {
    ...record,
    createdAt: record.createdAt || nowIso,
    updatedAt: nowIso,
    analysisVersionHistory: record.analysisVersionHistory.length > 0 ? record.analysisVersionHistory : [record.analysisVersion]
  };
  const persisted = await persistRecord(fresh);
  return {
    backend: persisted.backend,
    databaseStatus: persisted.status,
    record: fresh,
    duplicateIdentity: {
      strategy: "none",
      matchedActivityId: null,
      mergedWithExisting: false,
      createdNewRecordBecauseIdConflict: false
    }
  };
}
